if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      connectedCallback() {
        // Move to <body> to escape any parent transforms (e.g. Dawn's scroll-reveal
        // animations) that break position:fixed by creating a new containing block.
        if (this.parentElement && this.parentElement !== document.body) {
          document.body.appendChild(this);
          return; // connectedCallback will fire again after re-attach
        }

        this.productSection = document.querySelector('.cx-product') || document.querySelector('[id^="MainProduct"]');
        if (!this.productSection) return;

        this.variantInput = this.querySelector('input[name="id"]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.submitBtn = this.querySelector('button[type="submit"]');
        this.submitText = this.submitBtn?.querySelector('span');
        this.selects = this.querySelectorAll('[data-sticky-option-index]');
        this.variants = [];

        try {
          this.variants = JSON.parse(this.dataset.variants || '[]');
        } catch { /* skip */ }

        // Show/hide based on scroll
        this.observer = new IntersectionObserver(
          ([entry]) => this.classList.toggle('is-visible', !entry.isIntersecting),
          { threshold: 0 }
        );
        this.observer.observe(this.productSection);

        // Sync FROM main product variant picker → sticky bar
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onMainVariantChange.bind(this));
        }

        // Sync FROM sticky bar selects → form
        this.selects.forEach((select) => {
          select.addEventListener('change', () => this.onStickySelectChange());
        });
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.unsubscribe) this.unsubscribe();
      }

      onMainVariantChange({ data }) {
        const variant = data?.variant;
        if (!variant) return;

        // Update hidden input
        if (this.variantInput) this.variantInput.value = variant.id;

        // Update price
        if (this.priceEl && variant.price != null) {
          this.priceEl.textContent = this.formatMoney(variant.price);
        }

        // Update selects to match
        if (variant.options) {
          this.selects.forEach((select, i) => {
            if (variant.options[i] != null) select.value = variant.options[i];
          });
        }

        // Update button state
        this.updateButton(variant.available);
      }

      onStickySelectChange() {
        const selectedOptions = Array.from(this.selects).map((s) => s.value);
        const variant = this.variants.find((v) =>
          v.options.every((opt, i) => opt === selectedOptions[i])
        );

        if (variant) {
          if (this.variantInput) this.variantInput.value = variant.id;

          if (this.priceEl) {
            this.priceEl.textContent = this.formatMoney(variant.price);
          }

          this.updateButton(variant.available);

          // Sync back to main product picker
          this.syncToMainPicker(selectedOptions);
        } else {
          this.updateButton(false);
        }
      }

      syncToMainPicker(selectedOptions) {
        const mainSelects = this.productSection.querySelector('variant-selects');
        if (!mainSelects) return;

        // Try radio buttons first (pill picker)
        const fieldsets = mainSelects.querySelectorAll('fieldset');
        if (fieldsets.length > 0) {
          fieldsets.forEach((fieldset, i) => {
            if (selectedOptions[i] == null) return;
            const radio = fieldset.querySelector(`input[value="${CSS.escape(selectedOptions[i])}"]`);
            if (radio && !radio.checked) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
          return;
        }

        // Fall back to select dropdowns
        const dropdownSelects = mainSelects.querySelectorAll('select');
        dropdownSelects.forEach((select, i) => {
          if (selectedOptions[i] != null && select.value !== selectedOptions[i]) {
            select.value = selectedOptions[i];
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      // Shopify.formatMoney does not exist on this theme (2026-09-30: it threw on every variant change, leaving the
      // old price, option and sold-out state in the bar). Cents -> the store's money format, e.g. "${{amount}}".
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const value = Number(cents) / 100;
        const num = (decimals, thousands, point) => {
          const [whole, frac] = value.toFixed(decimals).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? point + frac : '');
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          if (key === 'amount_no_decimals') return num(0, ',', '.');
          if (key === 'amount_with_comma_separator') return num(2, '.', ',');
          if (key === 'amount_no_decimals_with_comma_separator') return num(0, '.', ',');
          if (key === 'amount_with_apostrophe_separator') return num(2, "'", '.');
          return num(2, ',', '.');
        });
      }

      updateButton(available) {
        if (!this.submitBtn) return;
        if (available) {
          this.submitBtn.removeAttribute('disabled');
          if (this.submitText) this.submitText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        } else {
          this.submitBtn.setAttribute('disabled', 'disabled');
          if (this.submitText) this.submitText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }
    }
  );
}
